import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CONNECTOR_OPTIONS,
  connectorRequiresApiKey,
  type ConnectorProviderId,
} from "./connector-options";

type AddConnectorDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnect: (params: {
    provider: ConnectorProviderId;
    connectionName: string;
    apiKey?: string;
  }) => Promise<unknown>;
  isConnecting: boolean;
};

export function AddConnectorDialog({
  open,
  onOpenChange,
  onConnect,
  isConnecting,
}: AddConnectorDialogProps) {
  const [connectionName, setConnectionName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [selectedProvider, setSelectedProvider] =
    useState<ConnectorProviderId | null>(null);

  useEffect(() => {
    if (!open) {
      setConnectionName("");
      setApiKey("");
      setSelectedProvider(null);
    }
  }, [open]);

  const selectedOption = CONNECTOR_OPTIONS.find(
    (option) => option.id === selectedProvider,
  );
  const requiresApiKey = selectedProvider
    ? connectorRequiresApiKey(selectedProvider)
    : false;
  const canConnect =
    connectionName.trim().length > 0 &&
    !!selectedProvider &&
    (!requiresApiKey || apiKey.trim().length > 0);

  async function handleConnect() {
    if (!canConnect) {
      return;
    }

    try {
      await onConnect({
        provider: selectedProvider,
        connectionName: connectionName.trim(),
        apiKey: requiresApiKey ? apiKey.trim() : undefined,
      });
      onOpenChange(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to connect connector";
      toast.error(message);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add connector</DialogTitle>
          <DialogDescription>
            Connect an external service to your workspace.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6">
          <div className="grid gap-2">
            <Label htmlFor="connection-name">Connection name</Label>
            <Input
              id="connection-name"
              value={connectionName}
              placeholder="Untitled Connector"
              onChange={(event) => setConnectionName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  void handleConnect();
                }
              }}
            />
          </div>

          <div className="grid gap-3">
            <Label>Connector</Label>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {CONNECTOR_OPTIONS.map((option) => {
                const isSelected = selectedProvider === option.id;
                const Logo = option.Logo;

                return (
                  <button
                    key={option.id}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => {
                      setSelectedProvider(option.id);
                      if (!connectionName.trim()) {
                        setConnectionName(`Untitled ${option.name} Connector`);
                      }
                    }}
                    className={cn(
                      "flex flex-col items-center gap-3 rounded-xl border p-5 text-center transition-colors",
                      "hover:bg-muted/60 focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
                      isSelected
                        ? "border-primary bg-primary/5 ring-primary ring-2"
                        : "border-border bg-background",
                    )}
                  >
                    <Logo className="h-14 w-14" />
                    <span className="text-sm font-medium">{option.name}</span>
                  </button>
                );
              })}
            </div>
            {selectedOption ? (
              <p className="text-muted-foreground text-sm">
                {selectedOption.description}
              </p>
            ) : null}
          </div>

          {requiresApiKey && selectedOption ? (
            <div className="grid gap-2">
              <Label htmlFor="connector-api-key">
                {selectedOption.apiKeyLabel ?? "API key"}
              </Label>
              <Input
                id="connector-api-key"
                autoComplete="off"
                value={apiKey}
                placeholder="Paste your API key"
                onChange={(event) => setApiKey(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    void handleConnect();
                  }
                }}
              />
              {selectedOption.apiKeyHelpUrl ? (
                <p className="text-muted-foreground text-sm">
                  Find your API key in{" "}
                  <a
                    href={selectedOption.apiKeyHelpUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline-offset-4 hover:underline"
                  >
                    {selectedOption.name} settings
                  </a>
                  .
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleConnect()}
            disabled={!canConnect || isConnecting}
          >
            {isConnecting ? "Connecting…" : "Connect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
